/**
 * SMEFinancingReadiness — /sme/financing-readiness
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, CheckCircle2, ChevronRight } from "lucide-react";
import client from "@/api/client";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";

const STATUS_CONFIG = {
  finance_ready:                    { label: "Finance Ready",               color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  conditionally_financeable:        { label: "Conditionally Financeable",   color: "bg-teal-50 border-teal-200 text-teal-700" },
  on_track_with_risks:              { label: "On Track — Address Risks",    color: "bg-amber-50 border-amber-200 text-amber-700" },
  requires_technical_strengthening: { label: "Technical Strengthening Needed", color: "bg-orange-50 border-orange-200 text-orange-700" },
  technical_assistance_required:    { label: "Technical Assistance Required", color: "bg-orange-50 border-orange-200 text-orange-700" },
  needs_intervention:               { label: "Needs Intervention",          color: "bg-red-50 border-red-200 text-red-700" },
  at_risk:                          { label: "At Risk",                     color: "bg-red-50 border-red-200 text-red-700" },
  data_improvement_required:        { label: "Data Improvement Required",   color: "bg-red-50 border-red-200 text-red-700" },
};

const READINESS_ACTIONS = [
  { step: 1, label: "Calculate 3+ months of emissions data",    check: "periods_3plus", path: "/sme/monthly/inputs"  },
  { step: 2, label: "Lock all calculated periods",              check: "some_locked",   path: "/sme/monthly/inputs"  },
  { step: 3, label: "Achieve confidence score ≥75",             check: "conf_75plus",   path: "/sme/monthly/inputs"  },
  { step: 4, label: "Generate Institutional Periodic Report",   check: "report_gen",    path: "/sme-reports"         },
  { step: 5, label: "Upload evidence to Evidence Vault",        check: "evidence_vault",path: "/evidence"            },
];

export default function SMEFinancingReadiness() {
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [periods,   setPeriods]   = useState(null);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      client.get("/sme/periodic/portfolio?mode=financing").catch(() => null),
      client.get("/sme/periodic/periods").catch(() => null),
    ]).then(([pRes, perRes]) => {
      setPortfolio(pRes?.data || null);
      setPeriods(perRes?.data || null);
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const pd        = portfolio?.portfolio_decision;
  const outcome   = pd?.outcome_key || "data_improvement_required";
  const config    = STATUS_CONFIG[outcome] || STATUS_CONFIG.data_improvement_required;
  const cred      = portfolio?.credibility || {};
  const periods_list = periods?.periods || [];
  const locked_count = periods_list.filter(p => p.is_locked).length;
  const avg_conf     = cred.avg_confidence || 0;

  const checks = {
    periods_3plus:  periods_list.length >= 3,
    some_locked:    locked_count > 0,
    conf_75plus:    avg_conf >= 75,
    report_gen:     false,
    evidence_vault: false,
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="SME WORKSPACE"
        title="Financing Readiness"
        subtitle="Climate finance eligibility based on Portfolio Intelligence V3"
        backUrl="/sme/overview"
        backLabel="SME Workspace"
      />

      {/* Status banner */}
      <div className={`rounded-xl border p-5 ${config.color}`}>
        <p className="text-xs font-bold uppercase tracking-wider opacity-70 mb-1">Financing Readiness Status</p>
        <p className="text-xl font-bold">{config.label}</p>
        {pd?.description && <p className="text-xs mt-1 opacity-70">{pd.description}</p>}
        {pd?.portfolio_score != null && (
          <p className="text-xs font-semibold mt-2 opacity-70">Portfolio Score: {pd.portfolio_score}/100</p>
        )}
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Periods" value={periods_list.length.toString()} sub="Calculated" />
        <MetricCard label="Locked"  value={locked_count.toString()} sub="Immutable" color="text-emerald-700" />
        <MetricCard label="Avg Confidence" value={`${avg_conf.toFixed(0)}/100`} sub="Data quality" color={avg_conf >= 75 ? "text-emerald-700" : "text-amber-600"} />
      </div>

      {/* Readiness checklist */}
      <SectionCard title="Readiness Checklist" noPadding>
        {READINESS_ACTIONS.map(item => (
          <button key={item.step} onClick={() => navigate(item.path)}
            className="w-full flex items-center gap-3 px-5 py-3.5 border-b border-slate-50 last:border-0 hover:bg-slate-50 text-left transition-colors">
            {checks[item.check]
              ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
              : <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />}
            <p className={`text-sm flex-1 ${checks[item.check] ? "text-slate-900 font-medium" : "text-slate-400"}`}>
              {item.step}. {item.label}
            </p>
            <ChevronRight size={14} className="text-slate-300 flex-shrink-0" />
          </button>
        ))}
      </SectionCard>

      {/* Conditions to resolve */}
      {pd?.conditions?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <p className="text-xs font-semibold text-amber-700 mb-2">Conditions to Resolve</p>
          {pd.conditions.map((c, i) => (
            <p key={i} className="text-xs text-amber-700 mb-1">→ {c}</p>
          ))}
        </div>
      )}
    </div>
  );
}
